import { useState, useEffect, useMemo } from 'react';
import { Modal, Alert } from 'react-bootstrap';
import { MdSave } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { useSetError } from '@src/redux/modals';
import { AnomalyDetectionLambdaFetch, getUserFacingMessage } from '@src/utils/fetch-utils';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { emailSelector } from '@src/redux/user';
import { DEFAULT_ALERT_FORM_DATA, AlertFormData } from './anomalyDetectionConstants';
import {
  useBudgetOptions,
  useEmailAddresses,
  useSlackMembers,
  useSlackChannels,
  useUsedBudgetIds,
  useAlertPresets
} from './hooks/useAlertData';
import {
  mapGroupedAlertToFormData,
  GroupedAlert,
  convertFormDataToDatabaseAlerts
} from './utils/alertUtils';
import { AlertForm } from './components/AlertForm';

import './EditAlertModal.scss';

interface EditAlertModalProps {
  show: boolean;
  onHide: () => void;
  groupedAlert: GroupedAlert;
  onSuccess?: () => void;
}

export const EditAlertModal = ({ show, onHide, groupedAlert, onSuccess }: EditAlertModalProps) => {
  const company = useAppSelector(currentCompanySelector);
  const email = useAppSelector(emailSelector);
  const {
    data: budgetOptions = [],
    isLoading: isLoadingBudgets,
    error: budgetError
  } = useBudgetOptions(company);

  const {
    data: emailAddressOptions = [],
    isLoading: isLoadingEmailAddresses,
    error: emailAddressError
  } = useEmailAddresses();

  const {
    data: slackMemberOptions = [],
    isLoading: isLoadingSlackMembers,
    error: slackMembersError
  } = useSlackMembers();

  const {
    data: slackChannelOptions = [],
    isLoading: isLoadingSlackChannels,
    error: slackChannelsError
  } = useSlackChannels();

  const { data: usedBudgetIds = new Set<number>() } = useUsedBudgetIds(company);

  const {
    data: alertPresets = [],
    isLoading: isLoadingPresets,
    error: presetsError
  } = useAlertPresets(company);

  const filteredUsedBudgetIds = useMemo(() => {
    if (!groupedAlert || !usedBudgetIds.size) {
      return usedBudgetIds;
    }

    const currentBudgetId = groupedAlert.individualAlerts[0]?.parameters?.budgetId;
    if (!currentBudgetId) {
      return usedBudgetIds;
    }

    const filtered = new Set(usedBudgetIds);
    filtered.delete(currentBudgetId);
    return filtered;
  }, [usedBudgetIds, groupedAlert]);

  const [formData, setFormData] = useState(DEFAULT_ALERT_FORM_DATA);
  const [errors, setErrors] = useState<string[]>([]);
  const setError = useSetError();
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [isSqlValid, setIsSqlValid] = useState(false);

  const isFormValid = (): boolean => {
    if (!formData.alertName.trim()) {
      return false;
    }

    if (formData.alertType === 'budget') {
      if (!formData.budgetId || formData.budgetId <= 0) {
        return false;
      }

      const hasMonitoringType =
        formData.actual.enabled || formData.projected.enabled || formData.remainder.enabled;
      if (!hasMonitoringType) {
        return false;
      }

      if (formData.actual.enabled) {
        const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
        const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
        if (!actualUnder || !actualOver) {
          return false;
        }
      }

      if (formData.projected.enabled) {
        const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
        const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
        if (!projectedUnder || !projectedOver) {
          return false;
        }
      }

      if (formData.remainder.enabled) {
        const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
        const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
        if (!remainderUnder || !remainderOver) {
          return false;
        }
      }
    } else if (formData.alertType === 'custom') {
      if (!formData.customSql || !formData.customSql.trim()) {
        return false;
      }

      if (!isSqlValid) {
        return false;
      }

      if (!formData.startDate) {
        return false;
      }
    }

    if (!formData.email.enabled && !formData.slack.enabled) {
      return false;
    }

    if (formData.slack.enabled) {
      if (formData.slack.channels.length === 0 || formData.slack.members.length === 0) {
        return false;
      }
    }

    if (formData.email.enabled) {
      if (formData.email.addresses.length === 0) {
        return false;
      }
    }

    return true;
  };

  const presetOptions = alertPresets.map(preset => ({
    value: preset.id,
    label:
      preset.preset_name && preset.description
        ? `${preset.preset_name} | ${preset.description}`
        : preset.preset_name || '',
    presetData: preset.preset_data
  }));

  useEffect(() => {
    if (groupedAlert) {
      const mappedData = mapGroupedAlertToFormData(groupedAlert);
      if (mappedData) {
        setFormData(mappedData);
        if (mappedData.alertType === 'custom' && mappedData.customSql) {
          setIsSqlValid(true);
        } else {
          setIsSqlValid(false);
        }
      }
      setErrors([]);
      setShowSuccessPopup(false);
    } else {
      setFormData(DEFAULT_ALERT_FORM_DATA);
      setIsSqlValid(false);
    }
  }, [groupedAlert]);

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.alertName.trim()) {
      newErrors.push('Alert Name is required');
    }

    if (formData.alertType === 'budget') {
      if (!formData.budgetId || formData.budgetId <= 0) {
        newErrors.push('Budget selection is required');
      }

      const hasMonitoringType =
        formData.actual.enabled || formData.projected.enabled || formData.remainder.enabled;
      if (!hasMonitoringType) {
        newErrors.push(
          'At least one monitoring type (Actual, Projected, or Remainder) must be enabled'
        );
      }

      if (formData.actual.enabled) {
        const actualUnder = formData.actual.under.replace(/[^0-9]/g, '');
        const actualOver = formData.actual.over.replace(/[^0-9]/g, '');
        if (!actualUnder) {
          newErrors.push("Actual 'Under' threshold is required");
        }
        if (!actualOver) {
          newErrors.push("Actual 'Over' threshold is required");
        }
      }

      if (formData.projected.enabled) {
        const projectedUnder = formData.projected.under.replace(/[^0-9]/g, '');
        const projectedOver = formData.projected.over.replace(/[^0-9]/g, '');
        if (!projectedUnder) {
          newErrors.push("Projected 'Under' threshold is required");
        }
        if (!projectedOver) {
          newErrors.push("Projected 'Over' threshold is required");
        }
      }

      if (formData.remainder.enabled) {
        const remainderUnder = formData.remainder.under.replace(/[^0-9]/g, '');
        const remainderOver = formData.remainder.over.replace(/[^0-9]/g, '');
        if (!remainderUnder) {
          newErrors.push("Remainder 'Under' threshold is required");
        }
        if (!remainderOver) {
          newErrors.push("Remainder 'Over' threshold is required");
        }
      }
    } else if (formData.alertType === 'custom') {
      if (!formData.customSql || !formData.customSql.trim()) {
        newErrors.push('Custom SQL query is required');
      }

      if (!isSqlValid) {
        newErrors.push('Custom SQL query must be validated');
      }

      if (!formData.startDate) {
        newErrors.push('Start date is required');
      }
    }

    if (!formData.email.enabled && !formData.slack.enabled) {
      newErrors.push('At least one notification method (Slack or Email) must be enabled');
    }

    if (formData.slack.enabled) {
      if (formData.slack.channels.length === 0) {
        newErrors.push(
          'At least one Slack channel is required when Slack notifications are enabled'
        );
      }
    }

    if (formData.email.enabled) {
      if (formData.email.addresses.length === 0) {
        newErrors.push(
          'At least one email address is required when Email notifications are enabled'
        );
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      try {
        setIsSaving(true);

        const alertsForDatabase = convertFormDataToDatabaseAlerts(
          formData,
          company,
          budgetOptions,
          email,
          groupedAlert.groupIsActive,
          groupedAlert.individualAlerts[0]?.parameters
        );

        await AnomalyDetectionLambdaFetch('/alerts/group', {
          method: 'PUT',
          body: {
            alert_group_id: groupedAlert.alertGroupId,
            alerts: alertsForDatabase
          }
        });

        setShowSuccessPopup(true);
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        setError({
          title: "Couldn't update alert",
          message: getUserFacingMessage(error, "Couldn't update alert"),
          reportError: error
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    onHide();
  };

  const handleSuccessClose = () => {
    setShowSuccessPopup(false);
    onHide();
  };

  const handleFormDataChange = (newFormData: AlertFormData) => {
    if (
      newFormData.alertType === 'custom' &&
      formData.alertType === 'custom' &&
      newFormData.customSql !== formData.customSql &&
      isSqlValid
    ) {
      setIsSqlValid(false);
    }

    setFormData(newFormData);
  };

  const handlePresetChange = (presetId: string | null) => {
    setSelectedPresetId(presetId);
  };

  const handleSqlValidationChange = (isValid: boolean) => {
    setIsSqlValid(isValid);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size='xl' centered className='edit-alert-modal'>
        <Modal.Header>
          <Modal.Title>Edit Alert</Modal.Title>
          <div className='required-fields-note'>*Required fields marked with an asterisk.</div>
        </Modal.Header>

        <Modal.Body>
          {errors.length > 0 && (
            <Alert variant='danger' className='mb-3'>
              <ul className='mb-0'>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <AlertForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            budgetOptions={budgetOptions}
            emailAddressOptions={emailAddressOptions}
            slackMemberOptions={slackMemberOptions}
            slackChannelOptions={slackChannelOptions}
            usedBudgetIds={filteredUsedBudgetIds}
            presetOptions={presetOptions}
            selectedPresetId={selectedPresetId}
            onPresetChange={handlePresetChange}
            isLoadingBudgets={isLoadingBudgets}
            isLoadingEmailAddresses={isLoadingEmailAddresses}
            isLoadingSlackMembers={isLoadingSlackMembers}
            isLoadingSlackChannels={isLoadingSlackChannels}
            isLoadingPresets={isLoadingPresets}
            budgetError={budgetError}
            emailAddressError={emailAddressError}
            slackMembersError={slackMembersError}
            slackChannelsError={slackChannelsError}
            presetsError={presetsError}
            onSqlValidationChange={handleSqlValidationChange}
            initialSqlValid={isSqlValid}
          />
        </Modal.Body>

        <Modal.Footer className='modal-controls'>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            leadingElement={<MdSave />}
            disabled={isSaving || !isFormValid()}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccessPopup}
        onHide={handleSuccessClose}
        size='sm'
        centered
        className='success-popup-modal'
      >
        <Modal.Body className='text-center p-4'>
          <div className='success-icon mb-3'>
            <MdSave />
          </div>
          <h5 className='mb-3'>Alert Updated Successfully!</h5>
          <p className='text-muted mb-4'>
            Your budget alert &quot;{formData.alertName}&quot; has been updated and is now active.
          </p>
          <Button onClick={handleSuccessClose} size={Size.SM}>
            Continue
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
};
